import React from 'react';
import styled from 'styled-components';
import { StaticImage } from 'gatsby-plugin-image';
import { map } from 'styled-components-breakpoint';
import { p, pt, px, py, pb, mb, mt, my } from 'styled-components-spacing';

import { colors, Text, Heading } from 'themes/components';
import HighlightText from 'utils/HighlightText';
import MissingIcon from './icons/MissingIcon';
import AutomateIcon from './icons/AutomateIcon';
import SyncIcon from './icons/SyncIcon';

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  ${px(2)};
  ${py({ mobile: 5, desktop: 8, xl: 10 })};
`;

const TopSection = styled.div`
  align-items: center;
  justify-content: space-between;
  ${map(
    {
      tablet: 'flex'
    },
    (display) => `display: ${display};`
  )};
`;

const TopSectionText = styled.div`
  ${map(
    {
      tablet: '0 0 40%'
    },
    (flex) => `flex: ${flex};`
  )};
  & > span {
    font-weight: 550;
    letter-spacing: -0.1px;
  }
`;

const SectionHeading = styled(Heading)`
  color: ${colors.grey5};
  ${mt(0)};
  ${mb(1)};
`;

const SectionText = styled(Text)`
  color: ${colors.grey30};
  ${mt(0)};
`;

const SectionImage = styled.div`
  ${map(
    {
      tablet: '0 0 50%'
    },
    (flex) => `flex: ${flex};`
  )};
`;

const BottomSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  ${mt(4)};
  ${map(
    {
      mobile: '24px',
      tablet: '48px'
    },
    (gap) => `gap: ${gap};`
  )};
`;

const Item = styled.div`
  color: ${colors.grey30};
  ${map(
    {
      mobile: '0 0 100%',
      desktop: '0 0 30%'
    },
    (flex) => `flex: ${flex};`
  )};
`;

const Title = styled.div`
  color: ${colors.grey5};
  font-weight: 600;
  margin-top: 0;
  ${mb(0)};
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Tag = styled.span`
  font-size: 12px;
  font-weight: 500;
  padding: 4px 8px;
  background: linear-gradient(
    to top left,
    hsla(140, 58%, 58%, 0.15),
    hsla(195, 100%, 66%, 0.15)
  );
  border-radius: 6px;
  border: 1px solid ${colors.teal40}15;
`;

const HappyTeams = () => (
  <Container>
    <TopSection>
      <TopSectionText>
        <HighlightText
          direction="right bottom"
          stopOne={colors.pink40}
          stopTwo={colors.purple40}
          style={{ fontWeight: 500 }}
        >
          The right tool for the job
        </HighlightText>
        <SectionHeading variant="h2">Keep your teams happy</SectionHeading>
        <SectionText>
          Whether you use Perforce, Git, SVN or something else, none is set up
          for managing source assets.
        </SectionText>
        <SectionText>
          Project based version control systems are inherently unsuitable for
          managing binary files and lack functionality for artists.
        </SectionText>
        <SectionText>
          Mustack gives your art team file based versioning, so they can iterate
          fearlessly.
        </SectionText>
      </TopSectionText>
      <SectionImage>
        <StaticImage src="./images/file-history.png" alt="add alt text todo" />
      </SectionImage>
    </TopSection>
    <BottomSection>
      <Item>
        <Title>
          <SyncIcon fillColor={colors.teal40} />
          Fewer breaking builds
        </Title>
        <Text>
          With mudstack’s seamless versioning and file tracking, artists are
          less likely to break builds by changing file names. Version control
          happens in the background; artists just need to create.
        </Text>
      </Item>
      <Item>
        <Title>
          <MissingIcon fillColor={colors.teal40} />
          No more missing files
        </Title>
        <Text>
          Mudstack shows each user a comprehensive list of all their local
          changes. It’s easy for artists to check in changes to the files they
          care about. When everything is checked in, your project is future
          proof.
        </Text>
      </Item>
      <Item>
        <Title>
          <AutomateIcon fillColor={colors.teal40} />
          Automate your pipeline
          <Tag>
            <HighlightText
              direction="top right"
              stopOne={colors.teal40}
              stopTwo={colors.blue40}
            >
              In development
            </HighlightText>
          </Tag>
        </Title>
        <Text>
          Mudstack’s upcoming automations will make it easier for artists to
          focus on their art rather than the pipeline, and reduce the risk of
          manual errors that break builds and cause expensive delays.
        </Text>
      </Item>
    </BottomSection>
  </Container>
);

export default HappyTeams;
